import { ai } from "./ai";

export const SUPPORTED_PRIMITIVES = [
    "box", "cube", "sphere", "sphere_small", "cylinder", "cylinder_thin", "tube",
    "torus", "torus_knot", "cone", "pyramid", "wedge", "prism", "diamond", "gem",
    "tetra", "capsule",
];

const SYSTEM_PROMPT = `You are a professional CAD "shape builder" inside RooCAD. Convert the user's description into a NON-EMPTY JSON array of MULTIPLE 3D primitives that, placed together, visibly resemble the requested object — never a single bland box.

CONTEXT / TARGET SIZE: The build plate is 220 x 220 x 250 units (1 unit = 1 mm). The final object should be BUILT AT A CLEARLY VISIBLE SIZE, roughly 15-60 units across. The default footprint of each UN-SCALED primitive is ~1 unit. Use explicit numeric offsets in positions and meaningful non-uniform scales so the parts sit apart and assemble into a real shape instead of overlapping into one blob.

STRICT OUTPUT RULES:
1. Reply with ONLY the JSON array. No markdown, no code fences, no prose, no explanations before or after.
2. Return AT LEAST 2 parts; usually 3-12. Decompose the object into recognizable sub-features (body, head, legs, arms, wheels, canopy, wings, handle, fins, tabletop, legs, chimney, antenna, ...). Coordinate numbers so the pieces fit together.
3. Every element uses EXACTLY this schema:
   { "type": "<type>", "position": [x, y, z], "scale": [sx, sy, sz], "rotation": [rx, ry, rz], "color": "#rrggbb" }
   - rotation and color are optional.
   - position/scale/rotation are arrays of exactly 3 finite numbers. [0,0,0] is the center of the build plate; +Y is up.
4. type MUST be one of these exact strings: box, cube, sphere, sphere_small, cylinder, cylinder_thin, tube, torus, torus_knot, cone, pyramid, wedge, prism, diamond, gem, tetra, capsule.
5. Use NON-UNIFORM scales (e.g. a thin tabletop is scale [40, 3, 20]) and EXPLICIT, spread-out positions so distinct parts are separated (a tabletop at y=20, legs at y=8 around it, feet at y=3). Assign each part a distinct, sensible color.
6. NEVER collapse a complex request into a single box or single cylinder. NEVER return an empty array.

WORKED EXAMPLE — user asks: "a rocket standing on its launch pad"
[
  {"type":"cone","position":[0,58,0],"scale":[28,42,28],"color":"#ef4444"},
  {"type":"cylinder","position":[0,24,0],"scale":[24,50,24],"color":"#f8fafc"},
  {"type":"tube","position":[0,-4,0],"scale":[30,8,30],"color":"#334155"},
  {"type":"box","position":[0,-16,0],"scale":[72,6,72],"color":"#64748b"},
  {"type":"cylinder","position":[-46,-28,0],"scale":[8,34,8],"color":"#94a3b8"},
  {"type":"cylinder","position":[46,-28,0],"scale":[8,34,8],"color":"#94a3b8"}
]`;

const TYPE_ALIASES = {
    box: "box", cube: "box", cuboid: "box", rectangle: "box", rectangular: "box", square: "box", block: "box", slab: "box", plank: "box", panel: "box", plate: "box",
    sphere: "sphere", ball: "sphere", orb: "sphere", globe: "sphere",
    sphere_small: "sphere_small", bead: "sphere_small",
    cylinder: "cylinder", rod: "cylinder", pole: "cylinder", pillar: "cylinder", bar: "cylinder", coin: "cylinder", drum: "cylinder",
    cylinder_thin: "cylinder_thin", shaft: "cylinder_thin", axle: "cylinder_thin",
    tube: "tube", pipe: "tube",
    torus: "torus", donut: "torus", ring: "torus", washer: "torus",
    torus_knot: "torus_knot",
    cone: "cone", funnel: "cone", spike: "cone",
    pyramid: "pyramid",
    wedge: "wedge",
    prism: "prism", hexagon: "prism", hex_prism: "prism",
    diamond: "diamond", octahedron: "diamond",
    gem: "gem", dodecahedron: "gem", crystal: "gem",
    tetra: "tetra", tetrahedron: "tetra",
    capsule: "capsule", pill: "capsule",
};

class GeometryEngine {
    async generateFromPrompt(prompt, context = '') {
        const sys = context
            ? `${SYSTEM_PROMPT}\n\nLEARNED KNOWLEDGE (fetched from your account's Scrape-to-CAD knowledge base — apply it freely):\n${context}`
            : SYSTEM_PROMPT;
        const response = await ai.chat([
            { role: 'system', content: sys } ,
            { role: 'user', content: prompt },
        ]);
        return this.parseGeometry(response);
    }

    /* Parse + validate any AI response into a clean array of renderable parts.
       Throws descriptive errors so the UI never shows a false "success". */
    parseGeometry(raw) {
        if (!raw || typeof raw !== "string" || !raw.trim()) {
            throw new Error("Empty response — check that the AI provider is configured and reachable.");
        }
        const json = this.extractJsonArray(raw);
        if (json === null) {
            throw new Error("The AI did not return a JSON geometry array.");
        }
        let parts;
        try {
            parts = JSON.parse(json);
        } catch {
            throw new Error("The AI returned malformed JSON geometry.");
        }
        if (!Array.isArray(parts)) {
            throw new Error("The AI returned a single object — expected an array of parts.");
        }
        if (parts.length === 0) {
            throw new Error("The AI returned an empty geometry array — nothing to build.");
        }
        return parts.map((part, i) => this.normalizePart(part, i));
    }

    /* Remove markdown code fences and slice out the first well-formed [...] array. */
    extractJsonArray(text) {
        let t = text.trim().replace(/^```(?:json)?\s*/i, "").replace(/\s*```\s*$/i, "");
        const start = t.indexOf("[");
        if (start === -1) return null;
        const end = this.matchingBracket(t, start);
        if (end === -1) return null;
        return t.slice(start, end + 1);
    }

    /* Scan for the matching ] of the bracket at `start`, ignoring brackets inside strings. */
    matchingBracket(text, start) {
        let depth = 0, inString = false, escaped = false;
        for (let i = start; i < text.length; i++) {
            const ch = text[i];
            if (inString) {
                if (escaped) escaped = false;
                else if (ch === "\\") escaped = true;
                else if (ch === '"') inString = false;
                continue;
            }
            if (ch === '"') inString = true;
            else if (ch === "[") depth++;
            else if (ch === "]") {
                depth--;
                if (depth === 0) return i;
            }
        }
        return -1;
    }

    normalizePart(part, index) {
        if (!part || typeof part !== "object" || Array.isArray(part)) {
            throw new Error(`Part #${index + 1} is not a valid object.`);
        }
        return {
            type: this.normalizeType(part.type, index),
            position: this.numArray(part.position, 3, 0, "position", index),
            scale: this.numArray(part.scale, 3, 1, "scale", index),
            rotation: this.numArray(part.rotation, 3, 0, "rotation", index),
            color: this.normalizeColor(part.color),
        };
    }

    normalizeType(type, index) {
        const key = String(type ?? "").trim().toLowerCase().replace(/[\s_-]+/g, "_");
        if (!key) {
            console.warn(`[GeometryEngine] Missing type on part #${index + 1} — defaulting to box.`);
            return "box";
        }
        const mapped = TYPE_ALIASES[key];
        if (!mapped) {
            console.warn(`[GeometryEngine] Unknown primitive type "${type}" on part #${index + 1} — defaulting to box.`);
            return "box";
        }
        return mapped;
    }

    numArray(value, length, fallback, label, index) {
        if (!Array.isArray(value)) {
            console.warn(`[GeometryEngine] Missing or invalid ${label} on part #${index + 1} — using default.`);
            return Array(length).fill(fallback);
        }
        const out = [];
        for (let i = 0; i < length; i++) {
            const n = Number(value[i]);
            out.push(Number.isFinite(n) ? n : fallback);
        }
        return out;
    }

    normalizeColor(value) {
        if (typeof value === "string") {
            const hex = value.trim().replace(/^#/, "");
            if (/^[0-9a-fA-F]{3,8}$/.test(hex)) return `#${hex}`;
            return value.trim() || undefined; // three.js also accepts CSS color names.
        }
        if (Array.isArray(value) && value.length >= 3) {
            const [r, g, b] = value.map((v) => Math.round(Number(v)));
            if ([r, g, b].every((v) => Number.isFinite(v))) return `rgb(${r}, ${g}, ${b})`;
        }
        if (typeof value === "number" && Number.isFinite(value)) {
            return `#${(value & 0xffffff).toString(16).padStart(6, "0")}`;
        }
        return undefined;
    }

    /* Approx unit-space footprint [w, h, d] for each primitive type at scale=1.
       Mirrors PrimitiveGeometry() so centering can reason about real extents. */
    baseBounds(type) {
        switch (type) {
            case "sphere": return [1, 1, 1];
            case "sphere_small": return [0.4, 0.4, 0.4];
            case "cylinder": return [1, 1, 1];
            case "cylinder_thin": return [0.2, 1, 0.2];
            case "tube": return [0.6, 1, 0.6];
            case "torus": return [1.4, 0.4, 1.4];
            case "torus_knot": return [0.9, 0.9, 0.9];
            case "cone": return [1, 1, 1];
            case "pyramid": return [1.2, 1, 1.2];
            case "wedge": return [1, 1, 1];
            case "prism": return [1, 1, 1];
            case "diamond": return [1.2, 1.2, 1.2];
            case "gem": return [1.1, 1.1, 1.1];
            case "tetra": return [1.4, 1.4, 1.4];
            case "capsule": return [0.7, 1.2, 0.7];
            case "box":
            default: return [1, 1, 1];
        }
    }

    /* Center the AI output on the build-plate origin, seat every part on the
       floor (y >= 0), shrink IF it overflows the build volume, and enlarge IF
       it is far smaller than the plate (so low-fidelity output stays visible).
       Returns a fresh array so the caller can splice into the scene without
       mutating history originals. */
    centerAndFit(parts, build = { x: 220, y: 220, z: 250 }) {
        if (!Array.isArray(parts) || parts.length === 0) return parts;
        const fit = parts.map((p) => ({ ...p, position: [...p.position], scale: [...p.scale] }));

        // 1) aggregate bounding box across all parts
        let min = [Infinity, Infinity, Infinity];
        let max = [-Infinity, -Infinity, -Infinity];
        fit.forEach((p) => {
            const [bw, bh, bd] = this.baseBounds(p.type);
            const pos = this.numArray(p.position, 3, 0, "position", 0);
            const scl = this.numArray(p.scale, 3, 1, "scale", 0);
            const half = [Math.abs(bw * scl[0]) / 2, Math.abs(bh * scl[1]) / 2, Math.abs(bd * scl[2]) / 2];
            for (let i = 0; i < 3; i++) {
                min[i] = Math.min(min[i], pos[i] - half[i]);
                max[i] = Math.max(max[i], pos[i] + half[i]);
            }
        });

        // 2) shift centroid to horizontal origin and sit on the floor
        const cx = (min[0] + max[0]) / 2;
        const cz = (min[2] + max[2]) / 2;
        const floorShift = -min[1];
        fit.forEach((p) => {
            p.position = [p.position[0] - cx, p.position[1] + floorShift, p.position[2] - cz];
        });

        // 3) shrink (only) if it overflows build volume: printBed {x,y,z} maps to width/depth/height
        const span = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
        const volSpan = { x: build.x, z: build.y, h: build.z };
        const ratio = Math.min(
            volSpan.x / span[0],
            volSpan.z / span[2],
            volSpan.h / span[1]
        );
        const s = Math.min(1, (Number.isFinite(ratio) ? ratio : 1) * 0.85);
        if (s < 1) {
            fit.forEach((p) => {
                p.position = p.position.map((v) => v * s);
                p.scale = p.scale.map((v) => v * s);
            });
        }

        // 4) enlarge uniformly (only) when the assembly is far smaller than the
        //    build plate, so low-fidelity / unit-1 outputs read as a visible
        //    object instead of a microscopic dot that looks like "nothing".
        const maxFoot = Math.max(span[0], span[2]);
        const targetFoot = Math.max(volSpan.x, volSpan.z) * 0.45;
        if (maxFoot > 0 && maxFoot < targetFoot) {
            const up = Math.min(
                targetFoot / maxFoot,
                volSpan.h / Math.max(span[1], 1e-6)
            );
            fit.forEach((p) => {
                p.position = p.position.map((v) => v * up);
                p.scale = p.scale.map((v) => v * up);
            });
        }
        return fit;
    }

    addPrimitive(type, position = [0, 0, 0], scale = [1, 1, 1], color = "#3b82f6") {
        return { type, position, scale, color };
    }
}

export const geometryEngine = new GeometryEngine();
